import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Users, Search, Trash2, Pencil, X, Save, ChevronDown, ChevronRight, 
  User, UserPlus, Mail, Phone, Calendar, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCustomers = () => {
  const { getAuthHeader } = useAdminAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingSubaccount, setEditingSubaccount] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/customers`, {
        headers: getAuthHeader()
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Nie udało się pobrać klientów');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;
    
    try {
      await axios.put(
        `${API}/api/admin/customers/${editingCustomer.user_id}`, 
        {
          firstName: editingCustomer.firstName,
          lastName: editingCustomer.lastName,
          phone: editingCustomer.phone,
          phoneCode: editingCustomer.phoneCode,
          birthDate: editingCustomer.birthDate
        },
        { headers: getAuthHeader() }
      );
      toast.success('Klient zaktualizowany');
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zaktualizować');
    }
  };

  const handleUpdateSubaccount = async () => {
    if (!editingSubaccount) return;
    
    try {
      await axios.put(
        `${API}/api/admin/customers/subaccount/${editingSubaccount.subaccount_id}`, 
        {
          firstName: editingSubaccount.firstName,
          lastName: editingSubaccount.lastName,
          phone: editingSubaccount.phone,
          phoneCode: editingSubaccount.phoneCode,
          birthDate: editingSubaccount.birthDate
        },
        { headers: getAuthHeader() }
      );
      toast.success('Subkonto zaktualizowane');
      setEditingSubaccount(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zaktualizować');
    }
  };

  const handleDeleteCustomer = async (userId) => {
    try {
      await axios.delete(`${API}/api/admin/customers/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('Klient i wszystkie subkonta usunięte');
      setDeleteConfirm(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się usunąć');
    }
  };

  const handleDeleteSubaccount = async (subaccountId) => {
    try {
      await axios.delete(`${API}/api/admin/customers/subaccount/${subaccountId}`, {
        headers: getAuthHeader()
      });
      toast.success('Subkonto usunięte');
      setDeleteConfirm(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się usunąć');
    }
  };

  // Filter customers based on search - also search in subaccounts
  const filteredCustomers = customers.filter(c => {
    const searchLower = searchTerm.toLowerCase();
    
    // Search in main account
    const mainMatch = (
      c.email?.toLowerCase().includes(searchLower) ||
      c.firstName?.toLowerCase().includes(searchLower) ||
      c.lastName?.toLowerCase().includes(searchLower) ||
      c.phone?.includes(searchTerm) ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchLower)
    );
    
    if (mainMatch) return true;
    
    // Search in subaccounts
    const subMatch = c.subaccounts?.some(sub => 
      sub.firstName?.toLowerCase().includes(searchLower) ||
      sub.lastName?.toLowerCase().includes(searchLower) ||
      sub.phone?.includes(searchTerm) ||
      `${sub.firstName} ${sub.lastName}`.toLowerCase().includes(searchLower)
    );
    
    return subMatch;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pl-PL');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-primary" />
            Klienci
          </h1>
          <p className="text-gray-400 mt-1">
            Zarządzaj kontami klientów i ich subkontami ({customers.length} klientów)
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Szukaj po imieniu, nazwisku, emailu lub telefonie..."
          className="pl-10 bg-white/5 border-white/10"
        />
      </div>

      {/* Customers list */}
      <div className="space-y-3">
        {filteredCustomers.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center text-gray-400">
              {searchTerm ? 'Nie znaleziono klientów' : 'Brak klientów w bazie'}
            </CardContent>
          </Card>
        ) : (
          filteredCustomers.map((customer) => (
            <Card key={customer.user_id} className="bg-white/5 border-white/10 overflow-hidden">
              {/* Customer row */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedUser(expandedUser === customer.user_id ? null : customer.user_id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white">
                        {customer.firstName} {customer.lastName}
                      </span>
                      {customer.subaccounts?.length > 0 && (
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                          {customer.subaccounts.length} subkont
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {customer.email}
                      </span>
                      {customer.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {customer.phoneCode || '+48'} {customer.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {formatDate(customer.createdAt)}
                  </span>
                  {expandedUser === customer.user_id ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded content */}
              {expandedUser === customer.user_id && (
                <div className="border-t border-white/10 bg-black/20">
                  {/* Customer details / edit form */}
                  <div className="p-4 border-b border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-medium flex items-center gap-2">
                        <User className="w-4 h-4" /> Dane konta głównego
                      </h3>
                      <div className="flex gap-2">
                        {editingCustomer?.user_id === customer.user_id ? (
                          <>
                            <Button size="sm" onClick={handleUpdateCustomer} className="bg-green-600 hover:bg-green-500">
                              <Save className="w-4 h-4 mr-1" /> Zapisz
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingCustomer(null)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setEditingCustomer({...customer})}>
                              <Pencil className="w-4 h-4 mr-1" /> Edytuj
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-400 hover:text-red-300"
                              onClick={() => setDeleteConfirm({ type: 'customer', id: customer.user_id, name: `${customer.firstName} ${customer.lastName}` })}
                            >
                              <Trash2 className="w-4 h-4 mr-1" /> Usuń
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {editingCustomer?.user_id === customer.user_id ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-gray-400">Imię</Label>
                          <Input
                            value={editingCustomer.firstName || ''}
                            onChange={(e) => setEditingCustomer({...editingCustomer, firstName: e.target.value})}
                            className="bg-white/5 border-white/10"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400">Nazwisko</Label>
                          <Input
                            value={editingCustomer.lastName || ''}
                            onChange={(e) => setEditingCustomer({...editingCustomer, lastName: e.target.value})}
                            className="bg-white/5 border-white/10"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400">Telefon</Label>
                          <div className="flex gap-2">
                            <Input
                              value={editingCustomer.phoneCode || '+48'}
                              onChange={(e) => setEditingCustomer({...editingCustomer, phoneCode: e.target.value})}
                              className="bg-white/5 border-white/10 w-20"
                            />
                            <Input
                              value={editingCustomer.phone || ''}
                              onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}
                              className="bg-white/5 border-white/10 flex-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-400">Data urodzenia</Label>
                          <Input
                            type="date"
                            value={editingCustomer.birthDate?.split('T')[0] || ''}
                            onChange={(e) => setEditingCustomer({...editingCustomer, birthDate: e.target.value})}
                            className="bg-white/5 border-white/10"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <p className="text-white">{customer.email}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Telefon:</span>
                          <p className="text-white">{customer.phone ? `${customer.phoneCode || '+48'} ${customer.phone}` : '-'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Data urodzenia:</span>
                          <p className="text-white">{formatDate(customer.birthDate)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">FitNet ID:</span>
                          <p className="text-white font-mono text-xs">{customer.fitnetClientId || '-'}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Subaccounts */}
                  <div className="p-4">
                    <h3 className="text-white font-medium flex items-center gap-2 mb-4">
                      <UserPlus className="w-4 h-4" /> 
                      Subkonta ({customer.subaccounts?.length || 0})
                    </h3>
                    
                    {customer.subaccounts?.length > 0 ? (
                      <div className="space-y-3">
                        {customer.subaccounts.map((sub) => (
                          <div 
                            key={sub.subaccount_id} 
                            className="p-3 bg-white/5 rounded-lg"
                          >
                            {editingSubaccount?.subaccount_id === sub.subaccount_id ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div>
                                    <Label className="text-gray-400 text-xs">Imię</Label>
                                    <Input
                                      value={editingSubaccount.firstName || ''}
                                      onChange={(e) => setEditingSubaccount({...editingSubaccount, firstName: e.target.value})}
                                      className="bg-white/5 border-white/10 h-9"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">Nazwisko</Label>
                                    <Input
                                      value={editingSubaccount.lastName || ''}
                                      onChange={(e) => setEditingSubaccount({...editingSubaccount, lastName: e.target.value})}
                                      className="bg-white/5 border-white/10 h-9"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">Telefon</Label>
                                    <div className="flex gap-1">
                                      <Input
                                        value={editingSubaccount.phoneCode || '+48'}
                                        onChange={(e) => setEditingSubaccount({...editingSubaccount, phoneCode: e.target.value})}
                                        className="bg-white/5 border-white/10 w-16 h-9"
                                      />
                                      <Input
                                        value={editingSubaccount.phone || ''}
                                        onChange={(e) => setEditingSubaccount({...editingSubaccount, phone: e.target.value})}
                                        className="bg-white/5 border-white/10 flex-1 h-9"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">Data urodzenia</Label>
                                    <Input
                                      type="date"
                                      value={editingSubaccount.birthDate?.split('T')[0] || ''}
                                      onChange={(e) => setEditingSubaccount({...editingSubaccount, birthDate: e.target.value})}
                                      className="bg-white/5 border-white/10 h-9"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={handleUpdateSubaccount} className="bg-green-600 hover:bg-green-500 h-8">
                                    <Save className="w-3 h-3 mr-1" /> Zapisz
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingSubaccount(null)} className="h-8">
                                    <X className="w-3 h-3" /> Anuluj
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                                    <User className="w-4 h-4 text-purple-400" />
                                  </div>
                                  <div>
                                    <p className="text-white font-medium">{sub.firstName} {sub.lastName}</p>
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                      {sub.phone && (
                                        <span>{sub.phoneCode || '+48'} {sub.phone}</span>
                                      )}
                                      {sub.birthDate && (
                                        <span>{formatDate(sub.birthDate)}</span>
                                      )}
                                      {sub.fitnetClientId && (
                                        <span className="font-mono">FitNet: {sub.fitnetClientId}</span>
                                      )}
                                      {sub.isActivated && (
                                        <span className="text-green-400">Aktywne</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-8 w-8 p-0"
                                    onClick={() => setEditingSubaccount({...sub})}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                                    onClick={() => setDeleteConfirm({ 
                                      type: 'subaccount', 
                                      id: sub.subaccount_id, 
                                      name: `${sub.firstName} ${sub.lastName}`,
                                      parentName: `${customer.firstName} ${customer.lastName}`
                                    })}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Brak subkont</p>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-md bg-gray-900 border-red-500/30">
            <CardHeader>
              <CardTitle className="text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Potwierdź usunięcie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300">
                {deleteConfirm.type === 'customer' ? (
                  <>
                    Czy na pewno chcesz usunąć klienta <strong className="text-white">{deleteConfirm.name}</strong> oraz wszystkie jego subkonta?
                  </>
                ) : (
                  <>
                    Czy na pewno chcesz usunąć subkonto <strong className="text-white">{deleteConfirm.name}</strong>?
                    <br />
                    <span className="text-sm text-gray-500">
                      (należy do: {deleteConfirm.parentName})
                    </span>
                  </>
                )}
              </p>
              <p className="text-sm text-red-400">
                Ta operacja jest nieodwracalna!
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
                  Anuluj
                </Button>
                <Button 
                  className="bg-red-600 hover:bg-red-500"
                  onClick={() => {
                    if (deleteConfirm.type === 'customer') {
                      handleDeleteCustomer(deleteConfirm.id);
                    } else {
                      handleDeleteSubaccount(deleteConfirm.id);
                    }
                  }}
                >
                  Usuń
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
